<template>
    <span
        :id="name"
        role="radiogroup"
        :aria-label="label || undefined"
        class="isolate inline-flex rounded-md shadow-xs dark:shadow-none"
        :data-spec-class="dataSpecClass"
    >
        <button
            v-for="(option, index) in options"
            :key="option.key"
            type="button"
            role="radio"
            :aria-checked="model === option.key"
            :disabled="isDisabled"
            :class="optionClassObject(option.key, index)"
            :data-spec-class="`${dataSpecClass}__option`"
            :data-spec-value="option.key"
            @click="select(option.key)"
        >
            {{ option.label }}
        </button>
    </span>
</template>

<script setup lang="ts">
import { SelectOption } from '@/types';

interface Props {
    label?: string;
    isDisabled?: boolean;
    name?: string;
    dataSpecClass?: string;
    options?: SelectOption[];
}

const props = withDefaults(defineProps<Props>(), {
    label: '',
    isDisabled: false,
    name: undefined,
    dataSpecClass: 'segmented-control',
    options: () => [],
});

const model = defineModel<string>({ required: true });

const optionClassObject = (key: string, index: number) => {
    const isSelected = model.value === key;

    return {
        'relative inline-flex items-center whitespace-nowrap border px-3 py-2 text-sm font-semibold transition-ease focus:z-10': true,
        '-ml-px': index > 0,
        'rounded-l-md': index === 0,
        'rounded-r-md': index === props.options.length - 1,
        'z-10 border-blue-800 bg-blue-800 text-white shadow-md': isSelected,
        'border-muted bg-surface text-primary hover:bg-surface--light': !isSelected,
        'cursor-pointer': !props.isDisabled,
        'cursor-not-allowed opacity-50': props.isDisabled,
    };
};

const select = (key: string) => {
    if (props.isDisabled || model.value === key) {
        return;
    }

    model.value = key;
};
</script>
